import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'

interface PlatformConfigItem {
  key: string
  value: unknown
  type: 'string' | 'number' | 'boolean' | 'json'
  description: string | null
  category: string
  is_sensitive: boolean
  updated_at: string
  updated_by: string | null
}

// List all config
export function useConfig(category?: string) {
  return useQuery({
    queryKey: ['super-admin', 'config', category],
    queryFn: async (): Promise<PlatformConfigItem[]> => {
      let query = supabase
        .from('platform_config')
        .select('*')
        .order('category', { ascending: true })
        .order('key', { ascending: true })

      if (category) {
        query = query.eq('category', category)
      }

      const { data, error } = await query

      if (error) throw error

      return (data || []).map((item: any) => ({
        key: item.key,
        value: parseConfigValue(item.value, item.value_type),
        type: item.value_type,
        description: item.description,
        category: item.category,
        is_sensitive: item.is_sensitive,
        updated_at: item.updated_at,
        updated_by: item.updated_by,
      }))
    },
  })
}

// Get config categories
export function useConfigCategories() {
  return useQuery({
    queryKey: ['super-admin', 'config', 'categories'],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from('platform_config')
        .select('category')

      if (error) throw error

      const categories = [...new Set((data || []).map((item: any) => item.category))]
      return categories.sort()
    },
  })
}

// Get single config item
export function useConfigItem(key: string) {
  return useQuery({
    queryKey: ['super-admin', 'config', 'item', key],
    queryFn: async (): Promise<PlatformConfigItem> => {
      const { data: item, error } = await supabase
        .from('platform_config')
        .select('*')
        .eq('key', key)
        .single()

      if (error) throw error

      return {
        key: item.key,
        value: parseConfigValue(item.value, item.value_type),
        type: item.value_type,
        description: item.description,
        category: item.category,
        is_sensitive: item.is_sensitive,
        updated_at: item.updated_at,
        updated_by: item.updated_by,
      }
    },
    enabled: !!key,
  })
}

// Update config
export function useUpdateConfig() {
  const queryClient = useQueryClient()
  const { userProfile } = useAuth()

  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }): Promise<PlatformConfigItem> => {
      // Get current item to know the type
      const { data: current, error: fetchError } = await supabase
        .from('platform_config')
        .select('value_type')
        .eq('key', key)
        .single()

      if (fetchError) throw fetchError

      const stringValue = serializeConfigValue(value, current.value_type)

      const { data: item, error } = await supabase
        .from('platform_config')
        .update({
          value: stringValue,
          updated_by: userProfile?.id,
        })
        .eq('key', key)
        .select()
        .single()

      if (error) throw error

      return {
        key: item.key,
        value: parseConfigValue(item.value, item.value_type),
        type: item.value_type,
        description: item.description,
        category: item.category,
        is_sensitive: item.is_sensitive,
        updated_at: item.updated_at,
        updated_by: item.updated_by,
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['super-admin', 'config'] })
    },
  })
}

// Helper functions
function parseConfigValue(value: string, type: string): unknown {
  try {
    switch (type) {
      case 'number':
        return Number(value)
      case 'boolean':
        return value === 'true'
      case 'json':
        return JSON.parse(value)
      default:
        // Remove surrounding quotes if present
        if (value.startsWith('"') && value.endsWith('"')) {
          return value.slice(1, -1)
        }
        return value
    }
  } catch {
    return value
  }
}

function serializeConfigValue(value: unknown, type: string): string {
  switch (type) {
    case 'number':
      return String(value)
    case 'boolean':
      return String(value)
    case 'json':
      return JSON.stringify(value)
    default:
      return JSON.stringify(value)
  }
}
